import { Link } from "@tanstack/react-router";
import { Avatar } from "@/components/Avatar";
import { humanSize, timeAgo } from "@/lib/format";

export type FileCardFile = {
  id: string;
  name: string;
  ext: string;
  sizeBytes: number;
  uploadedAt: number | string;
  uploader: { id: string; name: string; image?: string | null };
  task?: { id: string; title: string; projectColor?: string } | null;
};

const CATEGORY: Record<string, string> = {
  pdf: "pdf",
  doc: "doc",
  docx: "doc",
  xls: "xls",
  xlsx: "xls",
  csv: "xls",
  png: "img",
  jpg: "img",
  jpeg: "img",
  gif: "img",
  svg: "img",
  key: "key",
  ppt: "key",
  pptx: "key",
  zip: "zip",
};
export function categoryOf(ext: string): string {
  return CATEGORY[ext.toLowerCase()] ?? "other";
}

function toSeconds(t: number | string): number {
  return typeof t === "string" ? Math.floor(new Date(t).getTime() / 1000) : t;
}

// Inline doc icon — matches design's `docIcon` SVG verbatim.
function DocIcon() {
  return (
    <svg className="ficon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 3.5h6l4 4v9a1 1 0 01-1 1H5a1 1 0 01-1-1v-12a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M11 3.5V7.5h4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export type FileCardProps = { file: FileCardFile };

export function FileCard({ file }: FileCardProps) {
  const cat = categoryOf(file.ext);
  const sec = toSeconds(file.uploadedAt);
  const isImg = cat === "img";
  return (
    <article className="fcard">
      <a
        className={`fc-preview fc-preview-${cat}`}
        href={`/api/attachments/${file.id}/raw`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${file.name}`}
      >
        {isImg ? (
          // T49: real thumbnail via the raw redirect (raster-only allowlist,
          // so this can never be an SVG). Broken loads fall back to the ext
          // badge that renders underneath.
          <img
            className="fc-thumb"
            src={`/api/attachments/${file.id}/raw`}
            alt=""
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <DocIcon />
        )}
        <span className={`fc-ext fc-ext-${cat} t-mono`}>
          {file.ext.toUpperCase()}
        </span>
      </a>
      <div className="fc-body">
        <div className="fc-name" title={file.name}>
          {file.name}
        </div>
        <div className="fc-meta">
          <Avatar
            id={file.uploader.id}
            name={file.uploader.name}
            size={18}
            image={file.uploader.image}
          />
          <span className="mtxt">
            {timeAgo(sec)} · {humanSize(file.sizeBytes)}
          </span>
        </div>
        {file.task ? (
          <Link
            to="/tasks/$taskId"
            params={{ taskId: file.task.id }}
            className="fc-task"
          >
            {file.task.projectColor ? (
              <span
                className="proj-dot"
                style={{ background: file.task.projectColor }}
              />
            ) : null}
            <span>{file.task.title}</span>
          </Link>
        ) : null}
      </div>
    </article>
  );
}
